/**
 * =============================================================================
 * Componente: AffiliateImportResultStep
 * Versão: 1.0.0
 *
 * Etapa 4 — exibe o resultado consolidado da importação (criados, aprovados,
 * ignorados, falhas) e a lista de erros por linha.
 * =============================================================================
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, XCircle, UserPlus, UserCheck, AlertTriangle } from 'lucide-react';
import { AffiliateImportResult } from '@/hooks/useAffiliateImport';

interface Props {
  result: AffiliateImportResult | null;
  onComplete: () => void;
}

export default function AffiliateImportResultStep({ result, onComplete }: Props) {
  if (!result) return null;

  // No fluxo assíncrono (v2 PGMQ), o card principal mostra que a importação
  // foi enfileirada com sucesso e direciona para acompanhar em Listas → Afiliados.
  const isEnqueued = !!result.enqueued && result.success;

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col items-center text-center py-4">
        {result.success ? (
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mb-3" />
        ) : (
          <AlertTriangle className="h-16 w-16 text-amber-500 mb-3" />
        )}
        <h3 className="text-xl font-semibold">
          {isEnqueued
            ? 'Importação enfileirada com sucesso!'
            : result.success
              ? 'Importação concluída!'
              : 'Falha ao enfileirar importação'}
        </h3>
        <p className="text-sm text-muted-foreground max-w-lg">
          {isEnqueued
            ? `${result.queuedItems ?? 0} afiliado(s) foram divididos em ${result.queuedBatches ?? 0} lote(s) e estão sendo processados em segundo plano. Acompanhe o andamento em Listas → Afiliados.`
            : `${result.created} afiliado(s) cadastrado(s) com sucesso.`}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-500" />
            <div>
              <p className="text-2xl font-bold text-emerald-600">
                {isEnqueued ? (result.queuedItems ?? 0) : result.created}
              </p>
              <p className="text-xs text-muted-foreground">
                {isEnqueued ? 'Enfileirados' : 'Criados'}
              </p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {isEnqueued ? (result.queuedBatches ?? 0) : result.approved}
              </p>
              <p className="text-xs text-muted-foreground">
                {isEnqueued ? 'Lotes na fila' : 'Ativados'}
              </p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-2xl font-bold">{result.skipped}</p>
              <p className="text-xs text-muted-foreground">Ignorados</p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-2xl font-bold text-destructive">{result.failed}</p>
              <p className="text-xs text-muted-foreground">
                {isEnqueued ? 'Inválidos (pré-validação)' : 'Falhas'}
              </p>
            </div>
          </div>
        </CardContent></Card>
      </div>

      {result.errors.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h4 className="font-medium mb-3">Erros</h4>
            <ScrollArea className="h-[240px] pr-3">
              <ul className="space-y-2 text-sm">
                {result.errors.map((e, i) => (
                  <li key={i} className="flex gap-2 border-b pb-2 last:border-0">
                    <span className="font-mono text-muted-foreground shrink-0">
                      {e.rowIndex >= 0 ? `L${e.rowIndex + 2}` : '—'}
                    </span>
                    {e.email && <span className="font-medium shrink-0">{e.email}</span>}
                    <span className="text-destructive flex-1">{e.message}</span>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Botão "Concluir" é renderizado pelo rodapé fixo do wizard. */}
    </div>
  );
}
